/**
 * Server-Sent Events (SSE) parser.
 *
 * Implements the subset of the
 * [HTML Living Standard event-stream parser](https://html.spec.whatwg.org/multipage/server-sent-events.html#parsing-an-event-stream)
 * the assistant API actually emits — `event:` + `data:` lines, blank
 * line as a frame terminator, `:` comment lines ignored. `id:` and
 * `retry:` fields are intentionally **not** processed: the API does
 * not send them today, and the widget has no use for them
 * (reconnection is owned by P1-058's higher-level error handling, not
 * by an EventSource-style auto-reconnect — we cannot use EventSource
 * at all because it does not support `Authorization` headers per
 * P1-057's auth boundary).
 *
 * The parser is intentionally generic: it has no knowledge of the
 * assistant API's `delta` / `complete` / `error` event names or the
 * JSON shapes carried in `data`. Mapping those into typed
 * {@link AssistantStreamEvent}s is the api-client's responsibility
 * (see `./api-client.ts`). Keeping the SSE-framing concern isolated
 * here makes the parser independently unit-testable against
 * synthetic streams and lets us swap in a different transport
 * without rewriting the wire format.
 *
 * Line-ending policy: we recognise `\n` as the line terminator, with
 * a trailing `\r` (i.e. `\r\n`) on each line stripped. Lone `\r` is
 * NOT treated as a terminator. This matches what the API emits
 * (.NET's `ServerSentEventsWriter` writes `\n`-terminated lines) and
 * keeps the streaming logic robust against `\r\n` straddling a chunk
 * boundary — a naïve "any of LF/CR/CRLF" split risks emitting a
 * spurious blank-frame for a CRLF that happened to land across two
 * `Uint8Array` chunks. Should the contract ever change, the
 * one-place fix is the `processLine` helper below.
 */

/**
 * One parsed SSE frame. Field names mirror the EventSource API so
 * the parser output is trivially adaptable if we ever swap to a
 * native EventSource transport.
 *
 * `event` defaults to `'message'` per the EventSource spec when the
 * server omits the `event:` line. The assistant API always sends an
 * explicit name, so this default is defensive only.
 *
 * `data` is the joined data payload — multiple `data:` lines per
 * frame are concatenated with `'\n'` separators per the spec.
 * Empty-data frames yield `data === ''` rather than being
 * suppressed; the api-client surface treats those as a protocol
 * violation.
 */
export interface SseEvent {
  readonly event: string;
  readonly data: string;
}

/**
 * Parse a `text/event-stream` body into an async sequence of
 * {@link SseEvent}s.
 *
 * The caller owns the lifecycle of the underlying request: aborting
 * via `AbortController` cancels the body's reader, which surfaces as
 * a normal iterator end here (no error thrown — the loop simply
 * exits when the reader signals `done` or the underlying read
 * rejects, and we don't translate that into an SSE event because the
 * caller already knows they cancelled).
 *
 * Errors during decoding (which would indicate a malformed UTF-8
 * stream) propagate as `TypeError` from `TextDecoder.decode` and the
 * iterator throws.
 *
 * @param body - Typically `Response.body` from a `fetch` call. Any
 *               `ReadableStream<Uint8Array>` works — tests construct
 *               synthetic ones from string chunks.
 */
export async function* parseSseStream(
  body: ReadableStream<Uint8Array>
): AsyncIterableIterator<SseEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');

  /* Mutable parser state. `eventName` and `dataLines` accumulate
     until a blank line dispatches them as one frame; `buffer` holds
     the trailing partial line not yet terminated by `\n`. */
  let buffer = '';
  let eventName = '';
  let dataLines: string[] = [];

  /**
   * Build the current frame from `eventName` + `dataLines`, reset
   * the accumulators, and return it. Returns `null` for a blank
   * line that occurs with no buffered data — those are not real
   * frames, just whitespace separators between frames or at the
   * start of the stream.
   */
  const flushFrame = (): SseEvent | null => {
    if (dataLines.length === 0 && eventName === '') {
      return null;
    }
    const frame: SseEvent = {
      event: eventName === '' ? 'message' : eventName,
      data: dataLines.join('\n'),
    };
    eventName = '';
    dataLines = [];
    return frame;
  };

  /**
   * Apply one already-extracted line (no terminator) to the parser
   * state. A line that is exactly the empty string is a frame
   * boundary — the caller must call {@link flushFrame} for it,
   * NOT this function, since frame dispatch is the only thing that
   * yields. Comment lines (leading `:`) are silently dropped.
   */
  const processLine = (line: string): void => {
    if (line === '' || line.startsWith(':')) {
      return;
    }
    const colonIdx = line.indexOf(':');
    const fieldName = colonIdx === -1 ? line : line.slice(0, colonIdx);
    let fieldValue = colonIdx === -1 ? '' : line.slice(colonIdx + 1);
    /* SSE spec: a single leading SPACE on the field value is
       stripped (so authors can write `data: foo` and get `foo`,
       not ` foo`). Subsequent spaces are preserved. */
    if (fieldValue.startsWith(' ')) {
      fieldValue = fieldValue.slice(1);
    }
    if (fieldName === 'event') {
      eventName = fieldValue;
    } else if (fieldName === 'data') {
      dataLines.push(fieldValue);
    }
    /* Other field names (`id`, `retry`, plus anything unknown) are
       intentionally ignored — see the file-level doc comment. */
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });

      /* Drain every complete line currently buffered. Looking only
         for `\n` (with `\r` stripped from the line tail) keeps us
         safe against a `\r\n` straddling a chunk boundary; if we
         saw `\n`-only here, the trailing `\r` from a possible
         `\r\n` is harmless because we strip it from the line. */
      let newlineIdx = buffer.indexOf('\n');
      while (newlineIdx !== -1) {
        let line = buffer.slice(0, newlineIdx);
        buffer = buffer.slice(newlineIdx + 1);
        if (line.endsWith('\r')) {
          line = line.slice(0, -1);
        }
        if (line === '') {
          const frame = flushFrame();
          if (frame !== null) {
            yield frame;
          }
        } else {
          processLine(line);
        }
        newlineIdx = buffer.indexOf('\n');
      }
    }

    /* Final flush after the underlying stream ends. Any bytes left
       in the decoder are pulled out (passing no argument finalises
       the decoder per the WHATWG Encoding spec). */
    buffer += decoder.decode();
    if (buffer.length > 0) {
      let line = buffer;
      buffer = '';
      if (line.endsWith('\r')) {
        line = line.slice(0, -1);
      }
      if (line !== '') {
        processLine(line);
      }
    }
    /* A well-formed stream ends with a blank line, which already
       dispatched the last frame in the loop above. Some servers
       close without that trailing terminator — flush once more so
       a final frame with no blank-line suffix is still surfaced. */
    const tail = flushFrame();
    if (tail !== null) {
      yield tail;
    }
  } finally {
    /* Release the reader's lock so a caller that wants to e.g.
       cancel the underlying fetch and then read the response again
       (unlikely but possible in error-recovery flows) can do so. */
    reader.releaseLock();
  }
}
